import React from 'react';
import ms from 'ms';
import { useInfiniteQuery } from 'react-query';

import useResourceList from './useResourceList';
import usePublishedResourceList from './usePublishedResourceList';
import mutationFactory from './mutationFactory';
import useQuery from './useQuery';

import api from '..';

export const usePagesList = (queryVariables = {}, queryConfig = {}) => {
  const queryResult = useResourceList({ name: 'pages', api: api.pages }, queryVariables, queryConfig);

  return {
    pages: queryResult.resolvedData,
    latestPages: queryResult.latestData,
    ...queryResult,
  };
};

export const usePublishedPagesList = (queryVariables = {}, queryConfig = {}) => {
  const queryResult = usePublishedResourceList({ name: 'pages', api: api.pages }, queryVariables, queryConfig);

  return {
    pages: queryResult.resolvedData,
    latestPages: queryResult.latestData,
    ...queryResult,
  };
};

const queryInfinitePageList = (queryVariables, fetchMoreVariable) =>
  api.pages.getList({ ...queryVariables, ...fetchMoreVariable });

export const usePagesInfiniteList = (queryVariables = {}, queryConfig = {}) => {
  const queryResult = useInfiniteQuery(['pagesInf', queryVariables], queryInfinitePageList, {
    ...queryConfig,
    getFetchMore: (lastGroup, allGroups) =>
      lastGroup.total > allGroups.reduce((total, current) => total + current.data.length, 0)
        ? { page: allGroups.length + 1 }
        : false,
  });

  const pages = React.useMemo(() => (queryResult.data || []).reduce((acc, current) => [...acc, ...current.data], []), [
    queryResult.data,
  ]);

  return {
    pages,
    total: queryResult.data?.[0]?.total || 0,
    ...queryResult,
  };
};

export const usePage = ({ id, locale = 'en' }, queryConfig = {}) => {
  const queryResult = useQuery(['page', { id, locale }], api.pages.getById, {
    ...queryConfig,
    enabled: id,
  });

  return {
    page: queryResult.data || {},
    ...queryResult,
  };
};

const createMutation = mutationFactory({ invalidate: 'pages' });

export const useDeletePage = createMutation(api.pages.remove);

export const useCreatePage = createMutation(api.pages.create);

export const useUpdatePage = createMutation(api.pages.update);

export const usePublishPage = createMutation(api.pages.publish);

export const useUnpublishPage = createMutation(api.pages.unpublish);

export const useDuplicatePage = createMutation(api.pages.duplicate);

export const usePageTypeList = (queryVariables = {}, queryConfig = {}) => {
  const { data: pageTypes = [], ...queryResult } = useQuery(['pageTypes', queryVariables], api.pages.getTypeList, {
    cacheTime: ms('5m'),
    queryConfig,
  });

  return {
    pageTypes,
    ...queryResult,
  };
};
